import React from 'react';
import { Result, Button } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import './ErrorBoundary.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      eventId: null 
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
      eventId: Date.now().toString() // Simple error ID for tracking
    });

    // In production, you could send this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error, { contexts: { errorInfo } });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      eventId: null 
    });
  };

  render() {
    if (this.state.hasError) {
      const { title, subtitle, level = 'page' } = this.props;
      
      // Component-level error boundary (smaller scope)
      if (level === 'component') {
        return (
          <div className="error-boundary error-boundary--component">
            <div className="error-boundary__content">
              <h4 className="error-boundary__title">
                {title || 'Component Error'}
              </h4>
              <p className="error-boundary__description">
                {subtitle || 'This component encountered an error and could not be displayed.'}
              </p>
              <Button 
                size="small" 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={this.handleReset}
                className="error-boundary__action"
              >
                Retry
              </Button>
            </div>
          </div>
        );
      }

      // Page-level error boundary (full page scope)
      return (
        <div className="error-boundary error-boundary--page">
          <Result
            status="500"
            title={title || "Oops! Something went wrong"}
            subTitle={subtitle || "We're sorry, but something unexpected happened. Please try refreshing the page or go back to the homepage."}
            extra={[
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={this.handleReload}
                key="reload"
              >
                Reload Page
              </Button>,
              <Button 
                icon={<HomeOutlined />}
                onClick={this.handleGoHome}
                key="home"
              >
                Go Home
              </Button>
            ]}
          />
          
          {/* Error details in development */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="error-boundary__details">
              <summary>Error Details (Development Only)</summary>
              <div className="error-boundary__error-info">
                <h4>Error:</h4>
                <pre>{this.state.error.toString()}</pre>
                
                <h4>Component Stack:</h4>
                <pre>{this.state.errorInfo.componentStack}</pre>
                
                <h4>Error ID:</h4>
                <code>{this.state.eventId}</code>
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping components with error boundary
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

export default ErrorBoundary;